import React, { useState } from 'react';
import { PLATFORM_ENVIRONMENTS } from '../../constants/environments';

/**
 * <OnPremLicenseForm> - Formular zum Ausstellen einer signierten ONPREM-Lizenz.
 * Deckt alle Felder des Backend-Modells OnPremLicenseIssue ab
 * (backend/app/routers/platform_admin.py).
 *
 * Props:
 *   onSubmit(body)  - async; body == OnPremLicenseIssue (leere optionale Felder entfernt).
 *   submitting      - bool.
 *   disabled        - bool (z.B. kein platform_admin).
 *   t               - Uebersetzer.
 */
const TOOLS = ['p4i', 'p4a'];
// 'onprem' ist in diesem OnPrem-Ausstell-Dialog zusaetzlich waehlbar (und Default);
// bewusst NUR hier ergaenzt, nicht in der globalen PLATFORM_ENVIRONMENTS-Konstante
// (die steuert SaaS-Tool-URLs/Lizenz-Umgebungen und soll davon unberuehrt bleiben).
const ENVIRONMENTS = [...PLATFORM_ENVIRONMENTS, 'onprem'];
const BINDINGS = ['soft', 'hard'];
const ENFORCEMENTS = ['enforce', 'warn'];
const ON_EXPIRY = ['readonly', 'block'];

// Knappe deutsche Feld-Erklaerungen (als Hover-Tooltip am Feld). Inhalte entsprechen
// dem Aussteller-Contract (onprem_license_issuer.build_claims / OnPremLicenseIssue).
const HINTS = {
  environment:
    'Laufzeitumgebung der Instanz. Fuer On-Prem-Auslieferungen "onprem" waehlen; ' +
    'dev/staging/prod/demo fuer gehostete Umgebungen. Landet im Claim deployment.environment.',
  binding:
    'soft = tolerant: erlaubt Reinstall/Umzug, Kopien werden ueber den Heartbeat erkannt. ' +
    'hard = strikte Host-/Fingerprint-Bindung. Empfehlung fuer die Ersteinrichtung: soft.',
  enforcement_mode:
    'enforce = Lizenzverstoss/Ablauf sperrt gemaess "Bei Ablauf". ' +
    'warn = nur warnen, nicht sperren (pilot-freundlich).',
  on_expiry:
    'Verhalten nach Ablauf inkl. Kulanzfrist: readonly = nur-lesen, block = komplett gesperrt. ' +
    'Feste Policy: 60 Tage Vorwarnung, danach 30 Tage Kulanz.',
  instance_id:
    'Optionaler Server-Fingerprint; leer -> automatisch (dep-<hex>). Dient nur dem ' +
    'server-seitigen Heartbeat-Tracking und steht NICHT im Lizenz-Claim/Host-Binding.',
};

const EMPTY = {
  tool_id: 'p4i',
  host: '',
  environment: 'onprem',
  binding: 'soft',
  instance_id: '',
  valid_until: '',
  enforcement_mode: 'enforce',
  on_expiry: 'readonly',
  owner_contact: '',
  notes: '',
};

export default function OnPremLicenseForm({ onSubmit, submitting = false, disabled = false, t }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.host.trim()) {
      setError(t('license.onprem.form.host_required', 'Host (FQDN) ist erforderlich'));
      return;
    }
    // Leere optionale Felder weglassen -> Backend-Defaults / ToolLicense-Fallback greifen.
    const body = {
      tool_id: form.tool_id,
      host: form.host.trim(),
      environment: form.environment,
      binding: form.binding,
      enforcement_mode: form.enforcement_mode,
      on_expiry: form.on_expiry,
    };
    if (form.instance_id.trim()) body.instance_id = form.instance_id.trim();
    if (form.valid_until) body.valid_until = form.valid_until;
    if (form.owner_contact.trim()) body.owner_contact = form.owner_contact.trim();
    if (form.notes.trim()) body.notes = form.notes.trim();
    await onSubmit(body);
  };

  const field = (labelKey, labelFallback, node, info) => (
    <div className="form-group" style={{ minWidth: 180, flex: 1 }}>
      <label className="field-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {t(labelKey, labelFallback)}
        {info && (
          <span
            title={info}
            aria-label={info}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 14, height: 14, borderRadius: '50%', cursor: 'help',
              fontSize: 10, fontWeight: 700, lineHeight: 1,
              border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
            }}
          >
            i
          </span>
        )}
      </label>
      {node}
    </div>
  );

  const selectNode = (key, options, labelPrefix) => (
    <select className="field-input" value={form[key]} onChange={set(key)} disabled={disabled}>
      {options.map((o) => (
        <option key={o} value={o}>
          {t(`${labelPrefix}.${o}`, o)}
        </option>
      ))}
    </select>
  );

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {field('license.onprem.form.tool', 'Tool', selectNode('tool_id', TOOLS, 'license.onprem.tool'))}
        {field('license.onprem.form.host', 'Host (FQDN)',
          <input
            className="field-input" type="text" value={form.host}
            onChange={set('host')} disabled={disabled}
            placeholder="p4i.kunde.example.com"
          />)}
        {field('license.onprem.form.environment', 'Umgebung',
          selectNode('environment', ENVIRONMENTS, 'license.onprem.env'),
          HINTS.environment)}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {field('license.onprem.form.binding', 'Bindung',
          selectNode('binding', BINDINGS, 'license.onprem.binding'),
          HINTS.binding)}
        {field('license.onprem.form.enforcement', 'Durchsetzung',
          selectNode('enforcement_mode', ENFORCEMENTS, 'license.onprem.enforcement'),
          HINTS.enforcement_mode)}
        {field('license.onprem.form.on_expiry', 'Bei Ablauf',
          selectNode('on_expiry', ON_EXPIRY, 'license.onprem.on_expiry'),
          HINTS.on_expiry)}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {field('license.onprem.form.valid_until', 'Gueltig bis (optional)',
          <input
            className="field-input" type="date" value={form.valid_until}
            onChange={set('valid_until')} disabled={disabled}
          />)}
        {field('license.onprem.form.instance_id', 'Instanz-ID (optional)',
          <input
            className="field-input" type="text" value={form.instance_id}
            onChange={set('instance_id')} disabled={disabled}
            placeholder={t('license.onprem.form.instance_id_ph', 'leer -> automatisch')}
          />,
          HINTS.instance_id)}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {field('license.onprem.form.owner', 'Kontakt / Owner (optional)',
          <input
            className="field-input" type="text" value={form.owner_contact}
            onChange={set('owner_contact')} disabled={disabled}
            placeholder={t('license.onprem.form.owner_ph', 'z. B. Vivavis, J. Doe')}
          />)}
        {field('license.onprem.form.notes', 'Notizen (optional)',
          <input
            className="field-input" type="text" value={form.notes}
            onChange={set('notes')} disabled={disabled}
            placeholder={t('license.onprem.form.notes_ph', 'freie Betriebsnotiz')}
          />)}
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.6 }}>
        {t('license.onprem.form.valid_until_hint',
           'Ohne Datum wird valid_until aus der ToolLicense des Mandanten uebernommen. Warn-Vorlauf 60 Tage, danach 30 Tage Kulanz (feste ADR-0018-Policy).')}
        <br />
        {t('license.onprem.form.autocreate_hint',
           'Fehlt fuer diesen Mandanten eine Tool-Lizenz, wird sie bei der Ausstellung automatisch angelegt (Default-Konditionen, aus den Angaben oben abgeleitet).')}
      </div>

      {error && (
        <div
          style={{
            background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
            borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)',
            marginTop: 12,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <button type="submit" className="btn btn-primary" disabled={disabled || submitting}>
          {submitting
            ? t('license.onprem.form.issuing', 'Stelle aus...')
            : t('license.onprem.form.submit', 'ONPREM-Lizenz ausstellen')}
        </button>
      </div>
    </form>
  );
}
